// RulesPage.jsx
import React from 'react';
import Seo, { breadcrumbLd } from '../components/Seo';
import PageHeader from '../components/PageHeader';
import { useLang } from '../i18n';

const C = {
  tr: {
    title: 'Şogi Kuralları',
    seoDesc: 'Şogi (Japon satrancı) kuralları: 9×9 tahta, terfi bölgesi, zorunlu terfi, elden taş sürme (drop), nifu, uchifuzume ve sennichite kurallarının Türkçe açıklaması.',
    lead: 'Tahta, terfi, elden taş sürme ve yasak hamleler: resmi kuralların sade özeti.',
    sections: [
      ['Tahta ve başlangıç', [
        'Tahta 9×9 = 81 kareden oluşur; satrançtan farklı olarak kareler tek renktir.',
        'Her oyuncu 20 taşla başlar: 1 şah, 2 altın, 2 gümüş general, 2 at, 2 mızrak, 1 kale, 1 fil ve 9 piyade.',
        'Taşlar sivri beşgen biçimindedir; sivri uç taşın ileri yönünü ve kime ait olduğunu gösterir.',
        "İlk hamleyi Sente (siyah) yapar.",
      ]],
      ['Terfi (Nari)', [
        "Her oyuncu için rakibin tarafındaki son üç sıra terfi bölgesidir.",
        'Şah ve altın dışındaki taşlar bu bölgeye girerken, bölge içinde hareket ederken veya bölgeden çıkarken terfi edebilir.',
        'Terfi edilen taş ters çevrilir (kırmızı yazılı yüz). Terfi geri alınamaz.',
        'Piyade ve mızrak son sıraya, at son iki sıraya giderse terfi zorunludur.',
      ]],
      ['Elden taş sürme (Uchi / Drop)', [
        "Ele geçirilen taş oyundan çıkmaz, onu alan oyuncunun eline geçer.",
        'Oyuncu sırası geldiğinde tahtada hamle yapmak yerine elindeki bir taşı herhangi bir boş kareye sürebilir.',
        'Sürülen taş her zaman terfisiz yüzüyle konur; terfi bölgesine sürülse bile ancak bir sonraki hareketinde terfi edebilir.',
        'Piyade ve mızrak son sıraya, at son iki sıraya sürülemez.',
      ]],
      ['Yasak hamleler', [
        'Nifu (二歩): Aynı sütunda terfisiz iki piyaden olamaz.',
        'Uchifuzume (打ち歩詰め): Elden piyade sürerek mat etmek yasaktır. Tahtadaki piyadeyi ilerleterek mat etmek serbesttir.',
        'Kendi şahını şaha bırakan hamle yapılamaz.',
      ]],
      ['Oyunun sonu', [
        'Mat edilen oyuncu kaybeder. Yasal hamlesi kalmayan oyuncu da kaybeder (pat yoktur).',
        'Sennichite (千日手): Aynı pozisyon dört kez tekrarlanırsa oyun berabere sayılır; sürekli şah çekerek tekrar yaratan taraf ise kaybeder.',
        'Jishōgi: İki şah da karşı terfi bölgesine girip mat edilemez hale gelirse taş sayımıyla karar verilir.',
      ]],
    ],
    implTitle: 'Sitemizdeki uygulama',
    impl: 'Oyunumuz kendi şahını şaha bırakan hamleleri, nifu ve uchifuzume yasaklarını otomatik uygular ve yalnızca yasal kareleri gösterir. Sennichite ve jishōgi henüz otomatik değerlendirilmiyor.',
  },
  en: {
    title: 'Shogi Rules',
    seoDesc: 'Shogi (Japanese chess) rules explained: the 9×9 board, promotion zone, forced promotion, drops, nifu, uchifuzume and sennichite.',
    lead: 'Board, promotion, drops and illegal moves: a plain summary of the official rules.',
    sections: [
      ['Board and setup', [
        'The board has 9×9 = 81 squares, all the same colour.',
        'Each player starts with 20 pieces: 1 king, 2 gold, 2 silver, 2 knights, 2 lances, 1 rook, 1 bishop and 9 pawns.',
        'Pieces are wedge-shaped; the point shows which way the piece faces and who owns it.',
        'Sente (Black) moves first.',
      ]],
      ['Promotion (Nari)', [
        "Each player's promotion zone is the last three ranks on the opponent's side.",
        'Any piece except the king and gold may promote when it moves into, within or out of that zone.',
        'A promoted piece is flipped over (red characters). Promotion cannot be undone.',
        'A pawn or lance reaching the last rank, or a knight reaching the last two ranks, must promote.',
      ]],
      ['Drops (Uchi)', [
        'A captured piece is not removed from play; it goes to the capturing player\'s hand.',
        'Instead of moving, a player may drop a piece from hand onto any empty square.',
        'Dropped pieces land unpromoted; even inside the promotion zone they can only promote on a later move.',
        'A pawn or lance cannot be dropped on the last rank, nor a knight on the last two ranks.',
      ]],
      ['Illegal moves', [
        'Nifu (二歩): you may not have two unpromoted pawns in the same file.',
        'Uchifuzume (打ち歩詰め): you may not checkmate by dropping a pawn. Checkmating by moving a pawn on the board is allowed.',
        'You may not make a move that leaves your own king in check.',
      ]],
      ['End of the game', [
        'A checkmated player loses. A player with no legal moves also loses (there is no stalemate).',
        'Sennichite (千日手): if the same position occurs four times the game is a draw; a player causing it with perpetual check loses.',
        'Jishōgi: if both kings enter the opposite promotion zones and cannot be mated, the result is decided by counting pieces.',
      ]],
    ],
    implTitle: 'How our game applies the rules',
    impl: 'Our game automatically prevents moves that leave your king in check, as well as nifu and uchifuzume, and only highlights legal squares. Sennichite and jishōgi are not yet adjudicated automatically.',
  },
};

export default function RulesPage() {
  const { lang, to, t } = useLang();
  const c = C[lang];

  return (
    <>
      <Seo
        title={c.title}
        description={c.seoDesc}
        jsonLd={breadcrumbLd([{ name: t('brand'), path: to('home') }, { name: c.title, path: to('rules') }])}
      />
      <PageHeader title={c.title} lead={c.lead} crumbs={[{ label: t('nav.rules') }]} />
      <div className="container">
        <div className="row g-3 g-md-4">
          {c.sections.map(([title, items]) => (
            <div className="col-12 col-lg-6" key={title}>
              <section className="panel prose h-100">
                <h2>{title}</h2>
                <ul className="mb-0">{items.map((s) => <li key={s} className="mb-2">{s}</li>)}</ul>
              </section>
            </div>
          ))}
          <div className="col-12 col-lg-6">
            <section className="panel prose h-100" style={{ borderTop: '4px solid var(--accent)' }}>
              <h2>{c.implTitle}</h2>
              <p className="mb-0">{c.impl}</p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
